import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './locales/en';

// The backend's LanguageService is the one list of supported languages
// (GET /api/v1/languages/supported). Here we only know which string files exist:
// a language is usable once it has src/i18n/locales/<code>.ts and a loader below.
export type SupportedLanguage = string;

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';
export const URL_LANGUAGE_STORAGE_KEY = 'PD-url-language';

// English is bundled; every other locale file is lazy-loaded on demand.
// One line per file in ./locales (the bundler needs static import paths).
const LOCALE_LOADERS: Record<string, () => Promise<{ default: typeof en }>> = {
    fr: () => import('./locales/fr'),
    es: () => import('./locales/es'),
    de: () => import('./locales/de'),
};

export const AVAILABLE_LANGUAGES: SupportedLanguage[] = [DEFAULT_LANGUAGE, ...Object.keys(LOCALE_LOADERS)];

export const isSupportedLanguage = (language: string | null | undefined): language is SupportedLanguage =>
    !!language && AVAILABLE_LANGUAGES.includes(language);

/** Native name of a language, e.g. "Français", for when the API list hasn't loaded. */
export const nativeLanguageName = (code: string): string => {
    const name = new Intl.DisplayNames([code], { type: 'language' }).of(code) ?? code;
    return name.charAt(0).toLocaleUpperCase(code) + name.slice(1);
};

/** Translation files per language. Not-yet-loaded languages point at English. */
export const TRANSLATION_FILES: Record<SupportedLanguage, typeof en> = Object.fromEntries(
    AVAILABLE_LANGUAGES.map((code) => [code, en])
);

const loaded = new Set<SupportedLanguage>([DEFAULT_LANGUAGE]);
export async function ensureLanguageResources(language: SupportedLanguage): Promise<void> {
    if (loaded.has(language) || !LOCALE_LOADERS[language]) return;
    const strings = (await LOCALE_LOADERS[language]()).default;
    TRANSLATION_FILES[language] = strings;
    i18n.addResourceBundle(language, 'translation', structuredClone(strings), true, true);
    loaded.add(language);
}

/**
 * BCP 47 locale per language for date/number formatting, from the API
 * (e.g. en -> en-GB). Until it loads, use the browser's locale when it is the
 * same language, else the bare code.
 */
let localeOverrides: Record<string, string> = {};
export const setLanguageLocales = (locales: Record<string, string>) => {
    localeOverrides = locales;
};
const currentLocale = (): string => {
    const lang = i18n.language || DEFAULT_LANGUAGE;
    if (localeOverrides[lang]) return localeOverrides[lang];
    const browser = typeof navigator !== 'undefined' ? navigator.language : '';
    return browser.split('-')[0] === lang ? browser : lang;
};

export const getLanguageFromSearch = (search: string): SupportedLanguage | null => {
    const lang = new URLSearchParams(search).get('lang');

    return isSupportedLanguage(lang) ? lang : null;
};

interface InitialLanguageOptions {
    search?: string;
    storedUrlLanguage?: string | null;
    storedTenantLanguage?: string | null;
}

export const getInitialLanguage = ({
    search = '',
    storedUrlLanguage = null,
    storedTenantLanguage = null,
}: InitialLanguageOptions): SupportedLanguage => {
    const searchLanguage = getLanguageFromSearch(search);
    if (searchLanguage) {
        return searchLanguage;
    }

    if (isSupportedLanguage(storedUrlLanguage)) {
        return storedUrlLanguage;
    }

    if (isSupportedLanguage(storedTenantLanguage)) {
        return storedTenantLanguage;
    }

    return DEFAULT_LANGUAGE;
};

export const getInitialLanguageFromBrowser = (): SupportedLanguage => {
    if (typeof window === 'undefined') {
        return DEFAULT_LANGUAGE;
    }

    try {
        const storedTenant = localStorage.getItem('PD-tenant');
        const tenantLang = storedTenant
            ? (JSON.parse(storedTenant)?.default_language as string | undefined)
            : null;

        return getInitialLanguage({
            search: window.location.search,
            storedUrlLanguage: localStorage.getItem(URL_LANGUAGE_STORAGE_KEY),
            storedTenantLanguage: tenantLang,
        });
    } catch (error) {
        console.warn('Failed to read language from storage', error);
        return getInitialLanguage({ search: window.location.search });
    }
};

/**
 * Determine the initial language with the following priority:
 * 1. URL language param stored in localStorage (for email invite links)
 * 2. Tenant's default language from localStorage
 * 3. Default language (English)
 */
let initialLanguage: SupportedLanguage = DEFAULT_LANGUAGE;
initialLanguage = getInitialLanguageFromBrowser();

i18n
    // Pass the i18n instance to react-i18next
    .use(initReactI18next)
    // Init i18next
    .init({
        resources: {
            en: { translation: structuredClone(en) },
        },
        lng: initialLanguage, // Start with tenant default when available, LanguageContext will update from API
        fallbackLng: DEFAULT_LANGUAGE,
        supportedLngs: AVAILABLE_LANGUAGES,

        interpolation: {
            // React already escapes values
            escapeValue: false,
        },

        // Debug mode in development
        debug: process.env.NODE_ENV === 'development',
    });

void ensureLanguageResources(initialLanguage);

export default i18n;

/**
 * Helper to format dates according to the current locale
 */
export const formatDate = (date: Date | string, options?: Intl.DateTimeFormatOptions): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const locale = currentLocale();

    return dateObj.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        ...options,
    });
};

/**
 * Helper to format numbers according to the current locale
 */
export const formatNumber = (num: number, options?: Intl.NumberFormatOptions): string => {
    const locale = currentLocale();
    return num.toLocaleString(locale, options);
};

/**
 * Helper to format currency according to the current locale
 */
export const formatCurrency = (amount: number, currency = 'GBP'): string => {
    const locale = currentLocale();
    return amount.toLocaleString(locale, {
        style: 'currency',
        currency,
    });
};
